// Rating category weights for overall score calculation
export const RATING_WEIGHTS = {
  cleanliness: 2.0,    // Critical
  smell: 2.0,          // Critical
  privacy: 1.5,        // Important
  supplies: 1.5,       // Important
  spaciousness: 1.0,   // Standard
  accessibility: 1.0,  // Standard
  lighting: 1.0,       // Standard
  ambiance: 0.5,       // Nice-to-have
  safety: 0.5          // Nice-to-have
};

// Problems a reviewer can flag ("Current issues?" in the rating form).
// Reviews store the keys; the UI shows the labels.
export const REPORTED_ISSUE_LABELS = {
  outOfTP: '🧻 Out of TP',
  outOfSoap: '🧼 Out of soap',
  outOfTowels: '🤚 Out of towels',
  outOfOrder: '🚫 Broken/Out of order',
  veryCrowded: '👥 Very crowded'
};

// How long a reported issue keeps showing at the top of a listing.
export const ISSUE_WARNING_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Issues reviewers reported recently, for the warning box on a listing.
 * Derived from reviews rather than stored on the bathroom, so a warning
 * simply expires once its reports are older than the window.
 * @param {Array} reviews - Reviews with `reportedIssues` and `createdAtMs`
 *   (null = still saving, which counts as just now)
 * @param {number} [now] - Current time in ms
 * @returns {Array<{key: string, label: string, count: number}>} In label order
 */
export function getRecentIssueWarnings(reviews, now = Date.now()) {
  const counts = {};
  for (const review of reviews || []) {
    const reportedAt = review.createdAtMs ?? now;
    if (now - reportedAt > ISSUE_WARNING_WINDOW_MS) continue;
    for (const issue of new Set(review.reportedIssues || [])) {
      if (REPORTED_ISSUE_LABELS[issue]) counts[issue] = (counts[issue] || 0) + 1;
    }
  }
  return Object.keys(REPORTED_ISSUE_LABELS)
    .filter((key) => counts[key])
    .map((key) => ({ key, label: REPORTED_ISSUE_LABELS[key], count: counts[key] }));
}

// Total weight for normalization
const TOTAL_WEIGHT = Object.values(RATING_WEIGHTS).reduce((sum, w) => sum + w, 0);

/**
 * Calculate weighted overall score from category ratings
 * @param {Object} categoryScores - Object with category names as keys and scores (1-5) as values
 * @returns {number} Overall score from 1-5
 */
export function calculateOverallScore(categoryScores) {
  let weightedSum = 0;
  let activeWeightSum = 0;

  Object.entries(categoryScores).forEach(([category, score]) => {
    if (score !== null && score !== undefined && score > 0) {
      const weight = RATING_WEIGHTS[category] || 1.0;
      weightedSum += score * weight;
      activeWeightSum += weight;
    }
  });

  // If no ratings, return 0
  if (activeWeightSum === 0) return 0;

  // Return weighted average
  return weightedSum / activeWeightSum;
}

/**
 * Fold one new review score into a bathroom's stored average rating.
 * Seeded bathrooms start at rating 0 / count 0, so the first review becomes
 * the average. Missing or invalid stored values are treated as "no reviews yet".
 * @param {number} currentRating - Stored average before this review
 * @param {number} currentCount - Number of reviews already counted
 * @param {number} newScore - The new review's overall score (1-5)
 * @returns {number} Updated average
 */
export function calculateRunningAverage(currentRating, currentCount, newScore) {
  const count = Number.isFinite(currentCount) && currentCount > 0 ? currentCount : 0;
  const rating = Number.isFinite(currentRating) ? currentRating : 0;
  return (rating * count + newScore) / (count + 1);
}

/**
 * Determine if score should show stars or poops
 * @param {number} score - Score from 1-5
 * @returns {string} "shiny" or "stinky"
 */
export function getScoreType(score) {
  return score >= 2.5 ? 'shiny' : 'stinky';
}

/**
 * Score type for a whole bathroom. A bathroom nobody has reviewed yet has a
 * stored rating of 0, which getScoreType would call "stinky" -- that put a
 * 💩 on ~24k seeded places that simply had no reviews. Unreviewed is its
 * own state instead.
 * @param {number} score - Average rating from 1-5 (0 when unrated)
 * @param {number} reviewCount - Number of reviews
 * @returns {string} "shiny", "stinky", or "unrated"
 */
export function getBathroomScoreType(score, reviewCount) {
  return reviewCount > 0 ? getScoreType(score) : 'unrated';
}

/**
 * Generate emoji stars or poops based on rating
 * @param {number} rating - Rating from 1-5
 * @param {string} type - "shiny" or "stinky", or auto-detect if not provided
 * @returns {string} Emoji string
 */
export function generateRatingEmojis(rating, type = null) {
  if (!rating || rating === 0) return '☆☆☆☆☆';

  const scoreType = type || getScoreType(rating);
  const fullCount = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;

  let emojis = '';

  if (scoreType === 'shiny') {
    // Use stars for good ratings (≥ 2.5)
    for (let i = 0; i < fullCount; i++) {
      emojis += '⭐';
    }
    if (hasHalf) {
      emojis += '✨';
    }
    while (emojis.length < 5) {
      emojis += '☆';
    }
  } else {
    // Use poops for bad ratings (< 2.5)
    for (let i = 0; i < fullCount; i++) {
      emojis += '💩';
    }
    if (hasHalf) {
      emojis += '💨'; // Half poop = fart cloud
    }
    while (emojis.replace(/💩|💨/g, 'x').length < 5) {
      emojis += '☆';
    }
  }

  return emojis;
}

/**
 * Generate rating display with number
 * @param {number} rating - Rating from 1-5
 * @param {number} reviewCount - Number of reviews
 * @returns {Object} Display object with emojis, type, and text
 */
export function getRatingDisplay(rating, reviewCount = 0) {
  const scoreType = getScoreType(rating);
  const emojis = generateRatingEmojis(rating, scoreType);
  const text = `${rating.toFixed(1)} (${reviewCount} review${reviewCount !== 1 ? 's' : ''})`;

  return {
    emojis,
    scoreType,
    rating: rating.toFixed(1),
    reviewCount,
    text,
    label: scoreType === 'shiny' ? 'Shiny Stars!' : 'Stinky Stars'
  };
}

/**
 * Rating display for a bathroom's overall score. Same as getRatingDisplay,
 * except a bathroom with no reviews reads as unrated instead of as a 0.0
 * "Stinky Stars" score.
 * @param {number} rating - Average rating from 1-5 (0 when unrated)
 * @param {number} reviewCount - Number of reviews
 * @returns {Object} Display object with emojis, type, and text
 */
export function getBathroomRatingDisplay(rating, reviewCount = 0) {
  if (reviewCount > 0) return getRatingDisplay(rating, reviewCount);
  return {
    emojis: '☆☆☆☆☆',
    scoreType: 'unrated',
    rating: null,
    reviewCount: 0,
    text: 'No reviews yet',
    label: 'Not rated yet'
  };
}

/**
 * Calculate average ratings from all reviews
 * @param {Array} reviews - Array of review objects
 * @returns {Object} Category averages and overall score
 */
export function calculateAverageRatings(reviews) {
  if (!reviews || reviews.length === 0) {
    return {
      categoryScores: {
        cleanliness: 0,
        smell: 0,
        privacy: 0,
        supplies: 0,
        spaciousness: 0,
        accessibility: 0,
        lighting: 0,
        ambiance: 0,
        safety: 0
      },
      overallScore: 0,
      scoreType: 'shiny'
    };
  }

  const categories = Object.keys(RATING_WEIGHTS);
  const categoryScores = {};

  // Calculate average for each category
  categories.forEach(category => {
    const ratings = reviews
      .map(r => r.detailedRatings?.[category] || r.quickRating)
      .filter(r => r !== null && r !== undefined && r > 0);

    categoryScores[category] = ratings.length > 0
      ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length
      : 0;
  });

  const overallScore = calculateOverallScore(categoryScores);
  const scoreType = getScoreType(overallScore);

  return {
    categoryScores,
    overallScore,
    scoreType
  };
}

/**
 * Badge definitions and unlock conditions
 */
export const BADGES = {
  // Beginner
  first_drop: {
    id: 'first_drop',
    name: 'First Drop',
    emoji: '💧',
    description: 'Submit your first review',
    condition: (stats) => stats.totalReviews >= 1
  },
  bathroom_scout: {
    id: 'bathroom_scout',
    name: 'Bathroom Scout',
    emoji: '📍',
    description: 'Check in to 5 bathrooms',
    condition: (stats) => stats.totalCheckIns >= 5
  },
  porcelain_photographer: {
    id: 'porcelain_photographer',
    name: 'Porcelain Photographer',
    emoji: '📸',
    description: 'Upload your first photo',
    condition: (stats) => stats.photosUploaded >= 1
  },
  shiny_finder: {
    id: 'shiny_finder',
    name: 'Shiny Finder',
    emoji: '⭐',
    description: 'Find a 4+ star bathroom',
    condition: (stats) => stats.shinyBathroomsFound >= 1
  },

  // Intermediate
  stink_detective: {
    id: 'stink_detective',
    name: 'Stink Detective',
    emoji: '💩',
    description: 'Report 10 bathroom issues',
    condition: (stats) => stats.issuesReported >= 10
  },
  throne_critic: {
    id: 'throne_critic',
    name: 'Throne Critic',
    emoji: '🚽',
    description: 'Leave 25 detailed reviews',
    condition: (stats) => stats.detailedReviews >= 25
  },
  bathroom_cartographer: {
    id: 'bathroom_cartographer',
    name: 'Bathroom Cartographer',
    emoji: '🗺️',
    description: 'Add 10 new bathrooms to the map',
    condition: (stats) => stats.bathroomsAdded >= 10
  },
  tp_tracker: {
    id: 'tp_tracker',
    name: 'TP Tracker',
    emoji: '🧻',
    description: 'Report supply issues 5 times',
    condition: (stats) => stats.supplyIssuesReported >= 5
  },
  royal_flush: {
    id: 'royal_flush',
    name: 'Royal Flush',
    emoji: '👑',
    description: '50 total reviews',
    condition: (stats) => stats.totalReviews >= 50
  },

  // Advanced
  bathroom_connoisseur: {
    id: 'bathroom_connoisseur',
    name: 'Bathroom Connoisseur',
    emoji: '🏆',
    description: '100 reviews with photos',
    condition: (stats) => stats.reviewsWithPhotos >= 100
  },
  five_star_general: {
    id: 'five_star_general',
    name: 'Five-Star General',
    emoji: '🌟',
    description: 'Find 50 bathrooms rated 4.5+',
    condition: (stats) => stats.shinyBathroomsFound >= 50
  },
  diamond_throne: {
    id: 'diamond_throne',
    name: 'Diamond Throne',
    emoji: '💎',
    description: 'Your reviews get 100+ helpful votes',
    condition: (stats) => stats.helpfulVotes >= 100
  },
  restroom_hero: {
    id: 'restroom_hero',
    name: 'Restroom Hero',
    emoji: '🦸',
    description: 'Help 10 owners fix issues',
    condition: (stats) => stats.ownersHelped >= 10
  },
  bullseye_bladder: {
    id: 'bullseye_bladder',
    name: 'Bullseye Bladder',
    emoji: '🎯',
    description: '365-day check-in streak',
    condition: (stats) => stats.longestStreak >= 365
  },

  // Special/Funny
  globe_trotter: {
    id: 'globe_trotter',
    name: 'Globe Trotter',
    emoji: '🌍',
    description: 'Review bathrooms in 10+ cities',
    condition: (stats) => stats.citiesVisited >= 10
  },
  night_owl: {
    id: 'night_owl',
    name: 'Night Owl',
    emoji: '🌙',
    description: 'Review 10 bathrooms after midnight',
    condition: (stats) => stats.midnightReviews >= 10
  },
  coffee_shop_connoisseur: {
    id: 'coffee_shop_connoisseur',
    name: 'Coffee Shop Connoisseur',
    emoji: '☕',
    description: 'Review 20 cafe bathrooms',
    condition: (stats) => stats.cafeReviews >= 20
  },
  emergency_responder: {
    id: 'emergency_responder',
    name: 'Emergency Responder',
    emoji: '🏃',
    description: 'Use "find nearest NOW" feature 5 times',
    condition: (stats) => stats.emergencySearches >= 5
  },
  germaphobe_gold: {
    id: 'germaphobe_gold',
    name: 'Germaphobe Gold',
    emoji: '🧼',
    description: 'Only review 4+ star bathrooms (50+ reviews)',
    condition: (stats) => stats.totalReviews >= 50 && stats.avgReviewRating >= 4
  },
  brave_soul: {
    id: 'brave_soul',
    name: 'Brave Soul',
    emoji: '💩',
    description: 'Review 10 bathrooms under 2 stars',
    condition: (stats) => stats.stinkyReviews >= 10
  },
  public_servant: {
    id: 'public_servant',
    name: 'Public Servant',
    emoji: '🎭',
    description: 'Review 25 public restrooms',
    condition: (stats) => stats.publicRestroomReviews >= 25
  }
};

/**
 * Get user level based on total points
 * @param {number} totalReviews - Total reviews submitted
 * @returns {Object} Level information
 */
export function getUserLevel(totalReviews) {
  const levels = [
    { level: 1, min: 0, max: 4, title: 'Bathroom Beginner', emoji: '💧' },
    { level: 5, min: 5, max: 10, title: 'Restroom Regular', emoji: '🚻' },
    { level: 10, min: 11, max: 20, title: 'Throne Enthusiast', emoji: '🚽' },
    { level: 20, min: 21, max: 30, title: 'Lavatory Legend', emoji: '👑' },
    { level: 30, min: 31, max: 50, title: 'Porcelain Professional', emoji: '💎' },
    { level: 50, min: 51, max: Infinity, title: 'Grand Master of the Throne', emoji: '🏆' }
  ];

  const level = levels.find(l => totalReviews >= l.min && totalReviews <= l.max) || levels[0];

  return {
    level: Math.min(Math.floor(totalReviews / 2) + 1, 51),
    displayLevel: level.level,
    title: level.title,
    emoji: level.emoji,
    progress: totalReviews,
    nextLevelAt: level.max === Infinity ? null : level.max + 1
  };
}

/**
 * Check which badges user has unlocked
 * @param {Object} userStats - User statistics object
 * @returns {Array} Array of unlocked badge IDs
 */
export function getUnlockedBadges(userStats) {
  return Object.values(BADGES)
    .filter(badge => badge.condition(userStats))
    .map(badge => badge.id);
}
